import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

interface MedicareCostBreakdownProps {
  readonly type: "partB" | "partD";
  readonly data: {
    readonly [key: string]: { readonly spent: string | number; readonly remaining: string | number; readonly max: string | number; readonly text: string; };
  };
}

export const MedicareCostBreakdown: FC<MedicareCostBreakdownProps> = ({ type, data }) => {
  const { t: translate } = useTranslation();

  const desktopView = useMemo(() => {
    return (
      <>
        <Grid style={{ borderBottom: "solid 1px black" }}>
          <Grid.Span columns={6} />
          <Grid.Span columns={2}>
            <Semibold>{translate("BENEFITS.medicareCostBreakdown.spentText")}</Semibold>
          </Grid.Span>
          <Grid.Span columns={2}>
            <Semibold>{translate("BENEFITS.medicareCostBreakdown.remainingText")}</Semibold>
          </Grid.Span>
          <Grid.Span columns={2}>
            <Semibold>
              {type === "partB" && translate("BENEFITS.medicareCostBreakdown.maxText")}
              {type === "partD" && translate("BENEFITS.medicareCostBreakdown.thresholdText")}
            </Semibold>
          </Grid.Span>
        </Grid>
        {Object.keys(data).map((key) => {
          return (
            <Grid key={key}>
              <Grid.Span columns={6}>
                <Paragraph testId={`title-${key}`} textAlign="left">{data[key].text}</Paragraph>
              </Grid.Span>
              <Grid.Span columns={2}>
                <Semibold testId={`spent-${key}`}>{data[key].spent}</Semibold>
              </Grid.Span>
              <Grid.Span columns={2}>
                <Semibold testId={`remaining-${key}`}>{data[key].remaining}</Semibold>
              </Grid.Span>
              <Grid.Span columns={2}>
                <Semibold testId={`max-${key}`}>{data[key].max}</Semibold>
              </Grid.Span>
            </Grid>
          );
        })}
      </>
    );
  }, [data, type]);

  const mobileView = useMemo(() => {
    return (
      <Stack>
        {Object.keys(data).map((key) => {
          return (
            <Stack key={key} spacing="mini">
              <Paragraph testId={`title-${key}`}>{data[key].text}</Paragraph>
              <Shelf>
                <Paragraph style={{ paddingLeft: "6px" }}>{translate("BENEFITS.medicareCostBreakdown.spentText")}</Paragraph>
                <Shelf.Spacer />
                <Semibold testId={`spent-${key}`}>{data[key].spent}</Semibold>
              </Shelf>
              <Shelf>
                <Paragraph style={{ paddingLeft: "6px" }}>{translate("BENEFITS.medicareCostBreakdown.remainingText")}</Paragraph>
                <Shelf.Spacer />
                <Semibold testId={`remaining-${key}`}>{data[key].remaining}</Semibold>
              </Shelf>
              <Shelf>
                <Paragraph style={{ paddingLeft: "6px" }}>
                  {type === "partB" && translate("BENEFITS.medicareCostBreakdown.maxText")}
                  {type === "partD" && translate("BENEFITS.medicareCostBreakdown.thresholdText")}
                </Paragraph>
                <Shelf.Spacer />
                <Semibold testId={`max-${key}`}>{data[key].max}</Semibold>
              </Shelf>
            </Stack>
          );
        })}
      </Stack>
    );
  }, [data, type]);

  const { content } = useResponsive({
    content: {
      mobile: mobileView,
      desktop: desktopView,
    },
  });

  return (
    <Stack spacing="mini">
      <Subheading>
        {type === "partB" && translate("BENEFITS.medicareCostBreakdown.medicarePartBText")}
        {type === "partD" && translate("BENEFITS.medicareCostBreakdown.medicarePartDText")}
      </Subheading>
      {content}
    </Stack>
  );
};
